
import React from "react"
import Button from "../../components/Button/Button"
import { getPracticeTestStatus, getShortTestDisplayName, getTestDisplayName, isEnhancedActTest } from "../../services/utils/practice-test-util"
import { allowEnhancedACTTest, isEnhanceACTTestAllowed } from "../../persistance/local"
import { UserInfo } from "../../services/account/account-rest-interface"
import "./TestSelectionDialog.css"

interface ITestSelectionDialogProps {
    userInfo: UserInfo
    testName: string
    onNavigate: (link) => void
    onCancel?: () => void
}

/**
 * Practice test dialog handles both SAT and EnhancedACT test selections.
 * 
 * @param props 
 * @returns 
 */
export function TestSelectionDialog(props: ITestSelectionDialogProps) {
    let {testName, onNavigate} = props
    let isEnhancedACT = isEnhancedActTest(testName)
    let testDisplayName = isEnhancedACT ? getShortTestDisplayName(testName): getTestDisplayName(testName)
    let imagePrefix = isEnhancedACT ? "act": "sat"
    let imgAlt = isEnhancedACT ? "ACT Test": "SAT Test"
    let image1 = `/assets/images/dashboard/practice/${imagePrefix}-online.png`
    let image2 = `/assets/images/dashboard/practice/${imagePrefix}-paperpen.png`

    if (!testName) {
        return <div>No test selected!</div>
    }

    function navigateToPaperTest() {
        onNavigate(`/app/practice-tests/instructions/${testName}`)
    }

    function navigateToVirtualTest() {
        onNavigate(`/app/practice-tests/virtual/${testName}`)
    }

    return (
        <div className=""> 
            <h4 className="section-title" style={{ maxWidth: isEnhancedACT ? '90%' : '578px' }}>Select if you want to take {isEnhancedACT && 'the Enhanced ACT '}{testDisplayName} in a digital or pencil-and-paper format.</h4>
            <div className="modal-cards-select text-center row">
                <div className="col-md-6 pl-md-0 pr-md-2 mb-3 mb-md-0">
                    <div className="modal-cards-select--card">
                        <div className="h-100 d-flex flex-column">
                            <div className="img-frame">
                                <img src={image1} alt={imgAlt} className="d-block mx-auto img-responsive" width="243" height="174" />
                            </div>
                            <p className="mx-auto" style={{ maxWidth: '220px' }}>Take {testDisplayName} online in a digital format</p>
                            <Button className="btn btn--text-white btn--bg-green" text="TAKE ONLINE" onClick={navigateToVirtualTest}/>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 pr-md-0 pl-md-2">
                    <div className="modal-cards-select--card">
                        <div className="h-100 d-flex flex-column">
                            <div className="img-frame alt">
                                <img src={image2} alt={imgAlt} className="d-block mx-auto img-responsive" width="243" height="174" />
                            </div>
                            <p className="mx-auto" style={{ maxWidth: '250px' }}>Take {testDisplayName} printed in a pencil-and-paper format</p>
                            <Button className="btn btn--text-white btn--bg-green" text="TAKE PENCIL AND PAPER" onClick={navigateToPaperTest}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


const PERMISSION_REQUIRED_FOR_TESTS = [
    "EnhancedACT2",
    "EnhancedACT3",
    "EnhancedACT4",
]

interface ITestSelectionDialogState {
    allowTest: boolean
}

export class ConditionalTestSelectionDialog extends React.Component<ITestSelectionDialogProps, ITestSelectionDialogState> {
    constructor(props) {
        super(props)
        this.state = {
            allowTest: false
        }
    }

    componentDidUpdate(prevProps: Readonly<ITestSelectionDialogProps>, prevState: Readonly<ITestSelectionDialogState>, snapshot?: any): void {
        let {testName: oldTestName} = prevProps
        let {testName} = this.props

        if (testName !== oldTestName) {
            let testAllowed = true
            if (PERMISSION_REQUIRED_FOR_TESTS.includes(testName)) {
                let {id} = this.props.userInfo
                testAllowed = isEnhanceACTTestAllowed(id, testName)
            }

            this.setState({allowTest: testAllowed})
        }
    }

    allowTestTakingOptions = (testName: string) => {
        let {id} = this.props.userInfo
        allowEnhancedACTTest(id, testName)
        this.setState({allowTest: true})
    }

    cancelTest = () => {
        let {onCancel} = this.props
        if (onCancel) onCancel()
    }

    navigateToVirtualTest(testName: string) {
        let {onNavigate} = this.props
        onNavigate(`/app/practice-tests/virtual/${testName}`)
    }

    navigateToPaperTest(testName: string) {
        let {onNavigate} = this.props
        onNavigate(`/app/practice-tests/instructions/${testName}`)
    }

    render() {
        let {testName, onNavigate, onCancel} = this.props
        let isEnhancedACT = isEnhancedActTest(testName)
        let testDisplayName = isEnhancedACT ? getShortTestDisplayName(testName): getTestDisplayName(testName)
        let imagePrefix = isEnhancedACT ? "act": "sat"
        let imgAlt = isEnhancedACT ? "ACT Test": "SAT Test"
        let image1 = `/assets/images/dashboard/practice/${imagePrefix}-online.png`
        let image2 = `/assets/images/dashboard/practice/${imagePrefix}-paperpen.png`
        let {allowTest} = this.state

        return (
            <div className=""> 
                {allowTest ? 
                    <h4 className="section-title" style={{ maxWidth: isEnhancedACT ? '90%' : '578px' }}>Select if you want to take {isEnhancedACT && 'the Enhanced ACT '}{testDisplayName} in a digital or pencil-and-paper format.</h4>
                    :
                    <>
                        <h4 className="section-title" style={{ maxWidth: isEnhancedACT ? '90%' : '578px' }}>Book Purchase Verification</h4>
                        <div className="">
                            Per our agreement with ACT, these practice tests and explanations are available for students who've purchased ACT's Official Prep Guide. Please indicate below that you've purchased that book, so you can access these review materials.
                        </div>
                        <br/>
                        <div className="">
                            <Button className="btn btn--text-white btn--bg-green" text="YES" onClick={e => this.allowTestTakingOptions(testName)}/>
                            {" "}
                            <Button className="btn btn--text-white btn--bg-red" text="NO" onClick={this.cancelTest}/>
                        </div>
                    </>
                }

                {allowTest &&
                    <div className="modal-cards-select text-center row">
                        <div className="col-md-6 pl-md-0 pr-md-2 mb-3 mb-md-0">
                            <div className="modal-cards-select--card">
                                <div className="h-100 d-flex flex-column">
                                    <div className="img-frame">
                                        <img src={image1} alt={imgAlt} className="d-block mx-auto img-responsive" width="243" height="174" />
                                    </div>
                                    <p className="mx-auto" style={{ maxWidth: '220px' }}>Take {testDisplayName} online in a digital format</p>
                                    <Button className="btn btn--text-white btn--bg-green" text="TAKE ONLINE" onClick={e => this.navigateToVirtualTest(testName)}/>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 pr-md-0 pl-md-2">
                            <div className="modal-cards-select--card">
                                <div className="h-100 d-flex flex-column">
                                    <div className="img-frame alt">
                                        <img src={image2} alt={imgAlt} className="d-block mx-auto img-responsive" width="243" height="174" />
                                    </div>
                                    <p className="mx-auto" style={{ maxWidth: '250px' }}>Take {testDisplayName} printed in a pencil-and-paper format</p>
                                    <Button className="btn btn--text-white btn--bg-green" text="TAKE PENCIL AND PAPER" onClick={e => this.navigateToPaperTest(testName)}/>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        )
    }
}
